import React from 'react';

import { api } from '@api/reduxApi';

import { useAccount } from './useAccount';

export const FEATURE_DETAILS: Record<ToggleableFeature, { title: string; description: string; hidden?: boolean }> = {
  adding_candidates: {
    title: 'Adding candidates',
    description: 'The ability to add candidates to Great Question.',
    hidden: true
  },
  adding_candidates_via_csv: {
    title: 'Adding candidates via CSV',
    description: 'The ability to add candidates by importing a CSV into Great Question.'
  },
  external_recruitment: {
    title: 'External Recruitment',
    description:
      'The ability to submit and pay for external recruitment requests through our integration with Respondent.'
  },
  exporting_study_responses: {
    title: 'Exporting study responses',
    description: 'The ability to export study responses from Great Question.',
    hidden: true
  },
  incentives: {
    title: 'Incentives',
    description: 'The ability to fund and pay incentives through our integration with Tremendous.'
  },
  livestream: {
    title: 'Livestream',
    description:
      'The ability to have observers watch a live stream video of interviews conducted through Great Question.'
  },
  online_task: {
    title: 'Online task',
    description: 'The ability to create and run online task studies through Great Question.'
  },
  panel: {
    title: 'Panel',
    description: 'The ability to create and run panel studies through Great Question.'
  },
  repository: {
    title: 'Repository',
    description: 'The ability to access and manage repositories in Great Question.',
    hidden: true
  },
  repository_workspace: {
    title: 'Repository workspace',
    description: 'The ability to access and manage repository workspaces in Great Question.',
    hidden: true
  },
  repository_export_study_recordings: {
    title: 'Repository export study recordings',
    description: 'The ability to export study recordings from repositories in Great Question.',
    hidden: true
  },
  survey: {
    title: 'Survey',
    description: 'The ability to create and run survey studies through Great Question.'
  },
  unmoderated_test: {
    title: 'Unmoderated test',
    description: 'The ability to create and run unmoderated testing studies through Great Question.'
  },
  exporting_candidates_via_csv: {
    title: 'Exporting candidates via CSV',
    description: 'The ability to download candidates from studies or the candidate table as CSV.',
    hidden: true
  }
};

export const DISABLED_TOOLTIPS = {
  adding_candidates: 'The admin has restricted the ability to add candidates manually.',
  adding_candidates_via_csv: 'The admin has restricted the ability to add candidates via CSV.',
  invite_via_link: 'The admin has restricted the ability to invite candidates via link.'
};

type Result = Record<ToggleableFeature, boolean>;

const DEFAULT_RESULT = Object.keys(FEATURE_DETAILS).reduce<Result>(
  (acc, feature) => ({ ...acc, [feature]: false }),
  {} as Result
);

type ReturnType = {
  disabledFeatures: Result;
  setDisabledFeatures: (partial: Partial<Result>) => void;
};

export const useDisabledFeatures = (): ReturnType => {
  const { data } = api.useGetAccountDisabledFeaturesQuery();
  const { account } = useAccount();
  const features: ToggleableFeature[] = data?.disabled_features || account?.disabled_features || [];

  const computedResult = React.useMemo(() => {
    return !features
      ? DEFAULT_RESULT
      : features.reduce<Result>((acc, feature) => ({ ...acc, [feature]: true }), { ...DEFAULT_RESULT });
  }, [features.join(',')]);

  const [disabledFeatures, setDisabledFeatures] = React.useState<Result>(computedResult);

  React.useEffect(() => {
    setDisabledFeatures(computedResult);
  }, [computedResult]);

  const setDisabledFeaturesFromPartial = React.useCallback((partial: Partial<Result>) => {
    setDisabledFeatures((prev) => ({ ...prev, ...partial }));
  }, []);

  return { disabledFeatures, setDisabledFeatures: setDisabledFeaturesFromPartial };
};
